import React, { useState } from 'react'
import { Image, ImageStyle } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { BottomSheet } from '@src/components'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text } from '@src/components/base'
import { nutritionGuidanceImage } from '@src/assets/images'
import { userSelector } from '@src/selectors/app'
import { AppStackParamList } from '@src/navigation/types'
import { UserPropertyKind } from '@src/types'
import { useDispatchAsync, useSnack } from '@src/utils'
import { ByosVideoCallsUpsellStep } from '../constants'
import { getNextByosVideoCallsUpsellStep, recordByosVideoCallsUpsellInteraction } from '../utils'

export const NutritionGuidanceUpsellModal = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)
  const user = useSelector(userSelector)
  const [isUpdatingStep, setIsUpdatingStep] = useState(false)

  const updateStep = async () => {
    if (!user) {
      return
    }

    // Inbox OFF: this campaign is fully client-driven and persisted in local storage.
    if (!notificationInboxEnabled) {
      recordByosVideoCallsUpsellInteraction(ByosVideoCallsUpsellStep.InitialModal, user.id)
      return
    }

    const nextStep = getNextByosVideoCallsUpsellStep(ByosVideoCallsUpsellStep.InitialModal)

    if (!nextStep) {
      return
    }

    try {
      // Inbox ON: backend campaign orchestrator uses this user property as the source of truth.
      // Updating the property to the next step can trigger downstream scheduling/cancellation of inbox notifications.
      await dispatchAsync({
        type: 'users/upsertUserProperty',
        payload: {
          kind: UserPropertyKind.ByosVideoCallsUpsell,
          value: nextStep,
        },
      })
      // Refresh the user so modal visibility and local campaign state immediately reflect backend changes.
      await dispatchAsync({ type: 'users/fetch' })
    } catch {
      showSnack('Failed to update your campaign status.', null, 'error')
      throw new Error('failed_to_update_byos_video_calls_upsell_step')
    }
  }

  const onSeeIfYouQualifyPress = async () => {
    if (isUpdatingStep) {
      return
    }

    setIsUpdatingStep(true)

    try {
      await updateStep()
    } catch {
      // continue navigation when update fails so user is not blocked
    } finally {
      setIsUpdatingStep(false)
    }

    navigation.replace('InsuranceQuestionnaire', {
      skipIntroScreen: true,
    })
  }

  const onDismiss = async () => {
    if (isUpdatingStep) {
      return
    }

    if (user) {
      setIsUpdatingStep(true)

      try {
        await updateStep()
      } catch {
        // no-op
      } finally {
        setIsUpdatingStep(false)
      }
    }
  }

  return (
    <BottomSheet
      primaryButton={{
        text: 'See If You Qualify',
        onPress: onSeeIfYouQualifyPress,
        disabled: isUpdatingStep,
      }}
      showDismissButton
      onDismiss={onDismiss}
      dismissButtonText="No thanks"
      buttonDirection="column-reverse"
    >
      <Image source={nutritionGuidanceImage.imageSource} style={styles.image as ImageStyle} />
      <Text style={styles.title} type="title-2">
        Get professional nutrition guidance paid for by your insurance
      </Text>
      <Text style={styles.content} type="regular">
        Get access to a glucose-certified nutritionist with experience in analyzing CGM data. Most
        Nutrisense members pay $0 out-of-pocket. Easily check your coverage and get ready to boost
        your health journey.
      </Text>
    </BottomSheet>
  )
}

const themedStyles = StyleService.create({
  title: {
    textAlign: 'center',
    marginVertical: 16,
  },
  content: {
    textAlign: 'center',
    marginBottom: 16,
  },
  image: {
    aspectRatio: 3 / 2,
    width: '100%',
    height: undefined,
    borderRadius: 20,
  },
})
